/** MediaRecorder helper for support-chat voice notes. */

export const VOICE_NOTE_MAX_SECONDS = 90;

export type VoiceNoteScope = {
  MediaRecorder?: typeof MediaRecorder;
  navigator?: Navigator;
};

export function isVoiceNoteRecordingSupported(
  scope: VoiceNoteScope = globalThis as unknown as VoiceNoteScope,
): boolean {
  return (
    typeof scope.MediaRecorder === 'function' &&
    !!scope.navigator?.mediaDevices?.getUserMedia
  );
}

export function pickVoiceNoteMimeType(
  scope: VoiceNoteScope = globalThis as unknown as VoiceNoteScope,
): string {
  const MediaRecorderCtor = scope.MediaRecorder;
  const candidates = [
    'audio/webm;codecs=opus',
    'audio/webm',
    'audio/ogg;codecs=opus',
    'audio/ogg',
    'audio/mp4',
  ];
  if (!MediaRecorderCtor?.isTypeSupported) {
    return 'audio/webm';
  }
  for (const type of candidates) {
    if (MediaRecorderCtor.isTypeSupported(type)) {
      return type;
    }
  }
  return 'audio/webm';
}

export function voiceNoteExtensionForMime(mime: string): string {
  const base = (mime || '').split(';')[0].toLowerCase();
  if (base.includes('ogg')) return 'ogg';
  if (base.includes('mp4') || base.includes('m4a')) return 'm4a';
  if (base.includes('mpeg') || base.includes('mp3')) return 'mp3';
  if (base.includes('wav')) return 'wav';
  return 'webm';
}

export function formatVoiceNoteSeconds(totalSeconds: number): string {
  const safe = Math.max(0, Math.floor(totalSeconds));
  const minutes = Math.floor(safe / 60);
  const seconds = safe % 60;
  return `${minutes}:${seconds.toString().padStart(2, '0')}`;
}

export interface ComposerVoiceNoteListener {
  onRecordingChange(recording: boolean): void;
  onTick(seconds: number): void;
  onComplete(file: File): void;
  onError(message: string): void;
}

/**
 * Owns one MediaRecorder + mic stream at a time.
 * Call start() / stop(); dispose() on component destroy.
 */
export class ComposerVoiceNoteRecorder {
  private mediaStream: MediaStream | null = null;
  private mediaRecorder: MediaRecorder | null = null;
  private chunks: Blob[] = [];
  private timer: ReturnType<typeof setInterval> | null = null;
  private seconds = 0;
  private mimeType = 'audio/webm';
  private starting = false;

  constructor(
    private readonly listener: ComposerVoiceNoteListener,
    private readonly scope: VoiceNoteScope = globalThis as unknown as VoiceNoteScope,
    private readonly maxSeconds = VOICE_NOTE_MAX_SECONDS,
  ) {}

  get supported(): boolean {
    return isVoiceNoteRecordingSupported(this.scope);
  }

  get recording(): boolean {
    return this.mediaRecorder?.state === 'recording';
  }

  async toggle(): Promise<void> {
    if (this.recording || this.starting) {
      this.stop();
      return;
    }
    await this.start();
  }

  async start(): Promise<void> {
    if (this.recording || this.starting) return;
    if (!this.supported) {
      this.listener.onError('Voice notes are not supported in this browser.');
      return;
    }

    const mediaDevices = this.scope.navigator?.mediaDevices;
    const MediaRecorderCtor = this.scope.MediaRecorder;
    if (!mediaDevices?.getUserMedia || !MediaRecorderCtor) {
      this.listener.onError('Voice notes are not supported in this browser.');
      return;
    }

    this.starting = true;
    try {
      const stream = await mediaDevices.getUserMedia({ audio: true });
      this.mediaStream = stream;
      this.mimeType = pickVoiceNoteMimeType(this.scope);
      this.chunks = [];
      this.seconds = 0;

      const recorder = new MediaRecorderCtor(stream, {
        mimeType: this.mimeType,
      });
      this.mediaRecorder = recorder;

      recorder.ondataavailable = (event: BlobEvent) => {
        if (event.data?.size > 0) {
          this.chunks.push(event.data);
        }
      };

      recorder.onstop = () => {
        const mimeBase = this.mimeType.split(';')[0] || 'audio/webm';
        const blob = new Blob(this.chunks, { type: mimeBase });
        this.cleanupHardware();
        this.listener.onRecordingChange(false);

        if (blob.size === 0) {
          this.listener.onError('Recording was empty. Try again.');
          return;
        }

        const ext = voiceNoteExtensionForMime(mimeBase);
        const file = new File([blob], `voice-note-${Date.now()}.${ext}`, {
          type: mimeBase,
        });
        this.listener.onComplete(file);
      };

      recorder.onerror = () => {
        this.cleanupHardware();
        this.listener.onRecordingChange(false);
        this.listener.onError('Voice recording failed. Try again.');
      };

      recorder.start(250);
      this.listener.onRecordingChange(true);
      this.listener.onTick(0);
      this.timer = setInterval(() => {
        this.seconds += 1;
        this.listener.onTick(this.seconds);
        if (this.seconds >= this.maxSeconds) {
          this.stop();
        }
      }, 1000);
    } catch {
      this.cleanupHardware();
      this.listener.onRecordingChange(false);
      this.listener.onError(
        'Microphone permission denied. Allow access and try again.',
      );
    } finally {
      this.starting = false;
    }
  }

  stop(): void {
    if (this.timer) {
      clearInterval(this.timer);
      this.timer = null;
    }
    const recorder = this.mediaRecorder;
    if (recorder && recorder.state !== 'inactive') {
      try {
        recorder.stop();
      } catch {
        this.cleanupHardware();
        this.listener.onRecordingChange(false);
      }
      return;
    }
    this.cleanupHardware();
    this.listener.onRecordingChange(false);
  }

  dispose(): void {
    this.stop();
    this.chunks = [];
  }

  private cleanupHardware(): void {
    if (this.timer) {
      clearInterval(this.timer);
      this.timer = null;
    }
    this.mediaStream?.getTracks().forEach((track) => track.stop());
    this.mediaStream = null;
    this.mediaRecorder = null;
    this.chunks = [];
    this.seconds = 0;
  }
}
